import { useMemo } from 'react'
import { Person, Unit_Occupancy } from 'src/@types/graphql'

const useOccupancyToUse = (person: Person | undefined) => {
  const occupancyToUse = useMemo<Unit_Occupancy | undefined>(() => {
    if (!person || !person.unit_occupancy_tenants) return undefined

    const isResident = person.type === 'RESIDENT'
    const statusToFilter = isResident ? ['Current', 'Notice'] : ['Past']

    // Filter occupancies
    const filteredOccupancies = person.unit_occupancy_tenants.filter((uot) => {
      /*
      Current Resident (isResident):
        baseCondition - Status should be Current or Notice
        Moveout date should be null
        If the moveout date is null for more than one, then the one that was created most recently

      Past Resident:
        baseCondition - Status should be Past
        Moveout date should be not null
        If more than one occupancy with not null move out dates, then the one with most recent move_out date
      */
      const baseCondition =
        !uot.deleted &&
        !uot.unit_occupancy.deleted &&
        statusToFilter.includes(uot.unit_occupancy.status)

      if (isResident) {
        return (
          baseCondition &&
          uot.is_active &&
          uot.unit_occupancy.is_active &&
          uot.move_out_date === null
        )
      }
      return baseCondition && uot.unit_occupancy.move_out != null
    })

    // If no occupancies meet the criteria, return undefined
    if (filteredOccupancies.length === 0) return undefined

    // If only one occupancy meets the criteria, return it
    if (filteredOccupancies.length === 1)
      return filteredOccupancies[0].unit_occupancy

    // If multiple occupancies meet the criteria, sort them
    //  created_at (if person type is RESIDENT)
    //  moved_out date (if person type is PAST_RESIDENT)
    const sortedOccupancies = filteredOccupancies.sort((a, b) => {
      //active flag gives preferential treatment
      if (a.unit_occupancy.is_active && !b.unit_occupancy.is_active) {
        return -1
      }
      if (!a.unit_occupancy.is_active && b.unit_occupancy.is_active) {
        return 1
      }
      //if both active or inactive, use other parameters to decide
      if (isResident) {
        // Sort by created_at for RESIDENT
        const aCreated = new Date(a.created_at).getTime()
        const bCreated = new Date(b.created_at).getTime()

        /*
        created_at is absent whenever a caller's query does not select it,
        which makes this subtraction NaN. A NaN comparator is treated as 0,
        so the whole sort silently becomes a no-op and selection falls back to
        arbitrary DB row order. Tiebreak on occupancy id instead — the same
        last-resort ordering the PAST_RESIDENT branch below already uses.
        */
        if (
          Number.isNaN(aCreated) ||
          Number.isNaN(bCreated) ||
          aCreated === bCreated
        ) {
          return b.unit_occupancy.id - a.unit_occupancy.id
        }

        return bCreated - aCreated
      } else {
        //you actually want to select the most recent move out/move in because they're PAST
        return a.unit_occupancy.move_out
          ? b.unit_occupancy.move_out
            ? a.unit_occupancy.move_out > b.unit_occupancy.move_out
              ? -1
              : 1
            : //a has move_out but not b. check move_in?
              a.unit_occupancy.move_in
              ? b.unit_occupancy.move_in
                ? a.unit_occupancy.move_in > b.unit_occupancy.move_in
                  ? -1
                  : 1
                : //a has move_in but not b. default latest one?
                  a.unit_occupancy.id > b.unit_occupancy.id
                  ? -1
                  : 1
              : //a has no move_in. check if b does?
                b.unit_occupancy.move_in
                ? //choose b because it has move_in?
                  1
                : //neither has move_in  default latest one?
                  a.unit_occupancy.id > b.unit_occupancy.id
                  ? -1
                  : 1
          : //a has no move out
            b.unit_occupancy.move_out
            ? //chooes b because it has move out?
              1
            : //neither has move_out.  default latest one?
              a.unit_occupancy.id > b.unit_occupancy.id
              ? -1
              : 1
      }
    })

    //return activeOccupancy || undefined
    // Return the first (most recent) occupancy after sorting
    return sortedOccupancies[0].unit_occupancy
  }, [person?.unit_occupancy_tenants, person?.type])

  return {
    occupancyToUse,
  }
}

export default useOccupancyToUse
